import { faCircleInfo as faCircleInfoRegular } from "@fortawesome/pro-regular-svg-icons";
import {
  faBox,
  faCircleInfo,
  faExclamation,
  faExclamationCircle,
  faFlask,
  faLoader,
  faLocationDot,
  faPersonCircleQuestion,
  faTruckFast,
  IconDefinition,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IonIcon, NavContextState } from "@ionic/react";
import {
  Alert,
  Button,
  ButtonVariant,
  Card,
  Flex,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import dayjs, { Dayjs } from "dayjs";
import { openChat } from "../services/chatService";
import { colors } from "../theme/colors";
import { MedicationOrderStatusParams, Prescription } from "../typings";
import { ConsultReason, ConsultType, Medication } from "../typings/enums";
import inAppBrowser from "./browser";
import { informationCircleOutline } from "ionicons/icons";
import { ConsultVisitStatusEnum } from "@futurhealth/steadymd-api-client";
import { usePostHog } from "posthog-js/react";
import type { SmdMigrationCtaGuard } from "../hooks/useSmdMigrationInterceptor";
import { FORMATTED_PHONE_NUMBER } from "../data/phone";

export enum MedicationOrderStatus {
  Pending = "pending",
  Processing = "processing",
  Shipping = "shipping",
  Delivered = "delivered",
  OnHoldForLabs = "onHoldForLabs",
  OnHoldForPayment = "onHoldForPayment",
  Refunded = "refunded",
  Canceled = "canceled",
  RequiresConfirmation = "requiresConfirmation",
  ConfirmedDelivered = "confirmedDelivered",
  ProcessingCard = "processingCard",
  AsyncConfirmed = "async_confirmed",
  AsyncOnHold = "async_on_hold",
}

/** Minimal shape for missed-SLA detection (Firestore consult docs). */
export type ConsultMissedSlaLike = {
  reason?: string | null;
  reasonSlug?: string | null;
};

/**
 * True when consult reason indicates provider missed SLA (enum, slug, or loose string match).
 * Temporary monitoring: compare with PostHog rejected_consult_observed to retire loose match if unused.
 */
export function isMissedSlaReason(
  consult: ConsultMissedSlaLike | null | undefined
): boolean {
  if (!consult) return false;
  const reason = consult.reason ?? "";
  const slug = consult.reasonSlug ?? "";
  if (reason === ConsultReason.missed_sla) return true;
  if (slug === "missed-sla") return true;
  const normalized = reason.toLowerCase().replace(/\s+/g, " ").trim();
  return normalized.includes("missed sla");
}

export function determineMedicationOrderStatus({
  medicationOrderPayment,
  prescription,
  bundled = false,
  consult,
  pharmacyProvider,
}: MedicationOrderStatusParams) {
  // Handle async_messaging consults (created for refills, side effects, or after missed SLA)
  if (consult?.consultType === ConsultType.async_messaging) {
    const vs = consult.visitStatus;

    const isAsyncMessagingOnHold =
      vs === ConsultVisitStatusEnum.Waiting ||
      vs === ConsultVisitStatusEnum.ReadyToResume;

    if (isAsyncMessagingOnHold) {
      return MedicationOrderStatus.AsyncOnHold;
    }

    // List statuses that should flow through to prescription logic
    const shouldFlowThrough =
      vs === ConsultVisitStatusEnum.Completed ||
      vs === ConsultVisitStatusEnum.WaitingToSchedule;

    if (!shouldFlowThrough) {
      return MedicationOrderStatus.AsyncConfirmed;
    }
  }

  // Handle initial_async consults
  if (consult?.consultType === ConsultType.initial_async) {
    const vs = consult.visitStatus;
    const isMissedSla = isMissedSlaReason(consult);

    const isExplicitAsyncOnHold =
      (vs === ConsultVisitStatusEnum.Rejected && isMissedSla) ||
      vs === ConsultVisitStatusEnum.Waiting ||
      vs === ConsultVisitStatusEnum.ReadyToResume;

    if (isExplicitAsyncOnHold) {
      return MedicationOrderStatus.AsyncOnHold;
    }

    // List statuses that should flow through to prescription logic
    const shouldFlowThrough =
      vs === ConsultVisitStatusEnum.Completed ||
      vs === ConsultVisitStatusEnum.WaitingToSchedule;

    if (!shouldFlowThrough) {
      return MedicationOrderStatus.AsyncConfirmed;
    }
  }

  // Non-async flow - check prescription
  if (!prescription) {
    return MedicationOrderStatus.OnHoldForLabs;
  }

  const paymentStatus = bundled ? "succeeded" : medicationOrderPayment?.status;

  if (!medicationOrderPayment && !bundled)
    return MedicationOrderStatus.OnHoldForPayment;
  if (paymentStatus === "refunded") return MedicationOrderStatus.Refunded;
  if (paymentStatus === "canceled") return MedicationOrderStatus.Canceled;
  if (paymentStatus === "failed") return MedicationOrderStatus.OnHoldForPayment;
  if (isPaymentPending(paymentStatus))
    return MedicationOrderStatus.OnHoldForPayment;

  if (paymentStatus === "succeeded") {
    const status = determineSuccessfulPaymentStatus(prescription);
    return checkForRequiredConfirmation(status, prescription, pharmacyProvider);
  }

  return MedicationOrderStatus.Pending;
}

function determineSuccessfulPaymentStatus(
  prescription: Prescription
): MedicationOrderStatus {
  if (prescription.shipDate) return MedicationOrderStatus.Shipping;
  if (prescription.fillDate) return MedicationOrderStatus.Processing;
  if (
    prescription.holdReason ||
    (prescription.createdDate && !prescription.fillDate)
  ) {
    return MedicationOrderStatus.Pending;
  }
  return MedicationOrderStatus.Pending;
}

function isPaymentPending(paymentStatus: string | undefined): boolean {
  const pendingStatuses = new Set([
    "requires_payment_method",
    "requires_confirmation",
    "requires_action",
    "processing",
    "pending",
    "unknown",
  ]);
  return pendingStatuses.has(paymentStatus ?? "");
}

/** Normalize various createdDate shapes (ISO string, ms, seconds, Date, Firestore Timestamp) to Dayjs */
type FSTimestamp = {
  seconds: number;
  nanoseconds: number;
  toDate?: () => Date;
};
function toDayjs(input: unknown): Dayjs | null {
  if (!input) return null;

  // Firestore Timestamp-like
  const ts = input as FSTimestamp;
  if (typeof ts?.seconds === "number" && typeof ts?.nanoseconds === "number") {
    const d =
      typeof ts.toDate === "function"
        ? ts.toDate()
        : new Date(ts.seconds * 1000);
    return dayjs(d);
  }

  // Native Date
  if (input instanceof Date) return dayjs(input);

  // Number (seconds or ms)
  if (typeof input === "number") {
    const ms = input < 1e12 ? input * 1000 : input;
    return dayjs(ms);
  }

  // String (ISO or parseable)
  if (typeof input === "string") return dayjs(input);

  return null;
}

function checkForRequiredConfirmation(
  status: MedicationOrderStatus,
  prescription: Prescription,
  pharmacyProvider?: "pharmacy_hub" | "amazon" | "gifthealth" | "alto"
): MedicationOrderStatus {
  const FIVE_DAYS = 5;
  const ONE_WEEK_IN_DAYS = 7;

  const isProcessingOrShipping =
    status === MedicationOrderStatus.Processing ||
    status === MedicationOrderStatus.Shipping;

  // Check if this is a GiftHealth Pharmacy prescription
  const isGiftHealthPharmacy = pharmacyProvider === "gifthealth";

  // For GiftHealth Pharmacy: Show confirm delivery after 5 days regardless of fillDate
  if (
    isGiftHealthPharmacy &&
    prescription?.createdDate &&
    !prescription?.fillDate
  ) {
    const fiveDaysAgo = dayjs().subtract(FIVE_DAYS, "day");
    const created = toDayjs(prescription.createdDate);
    if (created && created.isBefore(fiveDaysAgo)) {
      return MedicationOrderStatus.RequiresConfirmation;
    }
    // If less than 5 days old, keep it in Pending status
    return MedicationOrderStatus.Pending;
  }

  // Original logic for non-GiftHealth pharmacies
  if (isProcessingOrShipping && prescription.createdDate) {
    const oneWeekAgo = dayjs().subtract(ONE_WEEK_IN_DAYS, "day");
    const created = toDayjs(prescription.createdDate);
    if (created && created.isBefore(oneWeekAgo)) {
      return MedicationOrderStatus.RequiresConfirmation;
    }
  }
  return status;
}

const highlightWords = (word: string, red = false) =>
  red ? (
    <span style={{ color: "var(--red)" }}>{word}</span>
  ) : (
    <span style={{ color: "var(--blurple)" }}>{word}</span>
  );

export const getCardHeader = (status: MedicationOrderStatus) => {
  switch (status) {
    case MedicationOrderStatus.AsyncConfirmed:
      return <span>Your medication is {highlightWords("confirmed")}</span>;
    case MedicationOrderStatus.Pending:
      return <span>Your medication is {highlightWords("pending")}</span>;
    case MedicationOrderStatus.Processing:
      return (
        <span>Your medication is being {highlightWords("processed")}</span>
      );
    case MedicationOrderStatus.Shipping:
      return <span>Your medication is being {highlightWords("shipped")}</span>;
    case MedicationOrderStatus.Delivered:
    case MedicationOrderStatus.ConfirmedDelivered:
      return (
        <span>Your medication has been {highlightWords("delivered")}</span>
      );
    case MedicationOrderStatus.OnHoldForLabs:
    case MedicationOrderStatus.OnHoldForPayment:
    case MedicationOrderStatus.ProcessingCard:
    case MedicationOrderStatus.AsyncOnHold:
      return (
        <span>Your medication order is {highlightWords("on hold", true)}</span>
      );
    case MedicationOrderStatus.Refunded:
      return (
        <span>
          Your medication order has been {highlightWords("refunded", true)}
        </span>
      );
    case MedicationOrderStatus.Canceled:
      return (
        <span>
          Your medication order has been {highlightWords("canceled", true)}
        </span>
      );
    case MedicationOrderStatus.RequiresConfirmation:
      return (
        <span>
          Your medication order requires {highlightWords("confirmation")}
        </span>
      );
    default:
      return "";
  }
};

const createAlert = (text: string, icon: IconDefinition) => {
  return (
    <Alert
      variant="light"
      color="var(--red)"
      styles={() => ({
        root: {
          borderRadius: "8px",
          backgroundColor: "rgba(255, 0, 0, 0.1)",
        },
        wrapper: {
          alignItems: "center",
        },
        body: {
          flex: 1,
        },
      })}
    >
      <Flex align="center" gap="md">
        <FontAwesomeIcon
          icon={icon}
          size="lg"
          style={{ flexShrink: 0, color: "var(--red)" }}
        />
        <Text c="var(--red)">{text}</Text>
      </Flex>
    </Alert>
  );
};

const createDescription = (text: JSX.Element, icon: IconDefinition) => {
  return (
    <Flex justify="center" gap="sm" align="center">
      <FontAwesomeIcon icon={icon} size="lg" />
      {text}
    </Flex>
  );
};

export const getCardDescription = (
  status: MedicationOrderStatus,
  hasAmazonPriorAuth: boolean
) => {
  switch (status) {
    case MedicationOrderStatus.AsyncConfirmed:
      return (
        <Text>
          Your intake and photos are being reviewed by your provider. We&apos;ll
          reach out if we need any additional information from you.
        </Text>
      );
    case MedicationOrderStatus.Pending: {
      if (!hasAmazonPriorAuth) {
        return createDescription(
          <Text>
            Order confirmed. Pharmacy processing may take{" "}
            {highlightWords("24-48 hours.")}
          </Text>,
          faLoader
        );
      }
      break;
    }
    case MedicationOrderStatus.Processing:
      return createDescription(
        <Text>
          Order created and is processing. Usually ships in{" "}
          {highlightWords("3-5 business days.")}
        </Text>,
        faBox
      );
    case MedicationOrderStatus.Shipping:
      return createDescription(
        <Text>
          Order was fulfilled and is shipping. Usually arrives in{" "}
          {highlightWords("1-2 business days.")}
        </Text>,
        faTruckFast
      );

    case MedicationOrderStatus.Delivered:
      return createDescription(
        <Text>
          Please confirm your delivery to update your program roadmap.
        </Text>,
        faLocationDot
      );
    case MedicationOrderStatus.OnHoldForLabs:
      return createAlert(
        "Your medication is on hold until the required lab work is completed.",
        faFlask
      );
    case MedicationOrderStatus.OnHoldForPayment:
      return createAlert(
        "Your medication is on hold until a payment is successfully processed",
        faExclamation
      );
    case MedicationOrderStatus.Refunded:
      return createDescription(
        <Text>
          Order refunded. Refund processing may take
          {highlightWords(" 7-10 business days.")}
        </Text>,
        faCircleInfo
      );

    case MedicationOrderStatus.Canceled:
      return createAlert(
        "Your prescription medication order has been canceled.",
        faExclamation
      );
    case MedicationOrderStatus.RequiresConfirmation:
      return createAlert(
        "We are unable to verify the delivery status of your order.",
        faExclamation
      );
    case MedicationOrderStatus.ProcessingCard:
      return createDescription(
        <Text>
          We’re processing your new card. You don’t need to do anything — it
          will be charged within
          {highlightWords(" 24 hours.")}
        </Text>,
        faLoader
      );
    case MedicationOrderStatus.AsyncOnHold:
      return createAlert(
        "Your provider needs more information to complete your consultation, and they've sent you a message with the details.",
        faExclamationCircle
      );
  }
};

export const getProgress = (status: MedicationOrderStatus) => {
  switch (status) {
    case MedicationOrderStatus.Processing:
      return 1;
    case MedicationOrderStatus.Shipping:
      return 2;
    case MedicationOrderStatus.Delivered:
    case MedicationOrderStatus.ConfirmedDelivered:
      return 3;
    default:
      return 0;
  }
};

export const getOrderStatus = (
  status: MedicationOrderStatus,
  hasAmazonPriorAuth: boolean,
  orderNumber?: string | undefined
) => {
  if (
    status === MedicationOrderStatus.OnHoldForLabs ||
    status === MedicationOrderStatus.OnHoldForPayment ||
    status === MedicationOrderStatus.ProcessingCard ||
    status === MedicationOrderStatus.AsyncOnHold
  ) {
    return <Text c={colors.darkGray}>Action Required</Text>;
  }
  if (hasAmazonPriorAuth) {
    return <Text c={colors.disabledGray}>Order isn&apos;t confirmed</Text>;
  }
  if (orderNumber) {
    return <Text c={colors.darkGray}>Order #{orderNumber}</Text>;
  }
  switch (status) {
    case MedicationOrderStatus.AsyncConfirmed:
      return <Text c={colors.disabledGray}>Order confirmed</Text>;
    default:
      return <Text c={colors.disabledGray}>Unknown</Text>;
  }
};

const renderButton = (
  isMobile: boolean,
  text: string,
  variant: ButtonVariant,
  onClick: () => void,
  disabled = false
) => (
  // TODO: add onClick handler to buttons once defined
  <Button
    variant={variant}
    onClick={() => onClick()}
    disabled={disabled}
    color="var(--blurple)"
    w={isMobile ? "100%" : "50%"}
    size="lg"
    radius="50px"
  >
    {text}
  </Button>
);

export const getActionButtons = ({
  status,
  isMobile,
  setShowDetails,
  navContext,
  hideShowDetailsBtn,
  handleOpenDeliveryModal,
  prescription,
  amazon: { hasAmazonPriorAuth, setAmazonDetails },
  migrationGuard,
}: {
  status: MedicationOrderStatus;
  isMobile: boolean;
  setShowDetails: () => void;
  navContext: NavContextState;
  hideShowDetailsBtn?: boolean;
  handleOpenDeliveryModal?: (status: MedicationOrderStatus) => void;
  prescription: Prescription | undefined;
  amazon: { hasAmazonPriorAuth: boolean; setAmazonDetails: () => void };
  posthog: ReturnType<typeof usePostHog>;
  /**
   * Routes non-migrated users to the migration full page instead of running
   * the CTA. Audited exceptions ("View Details", "Chat with Provider") are
   * resolved by the guard, so every CTA can be wrapped safely.
   */
  migrationGuard?: SmdMigrationCtaGuard;
}) => {
  const handleViewDetails = () => {
    setShowDetails();
  };

  const renderCta = (
    isMobileCta: boolean,
    text: string,
    variant: ButtonVariant,
    onClick: () => void
  ) =>
    renderButton(
      isMobileCta,
      text,
      variant,
      () => {
        if (migrationGuard?.interceptCta(text)) return;
        onClick();
      },
      migrationGuard?.isCtaDisabled(text)
    );

  switch (status) {
    case MedicationOrderStatus.AsyncConfirmed:
      if (hideShowDetailsBtn) {
        return null;
      }
      return renderCta(isMobile, "View Details", "light", handleViewDetails);
    case MedicationOrderStatus.Pending:
    case MedicationOrderStatus.Processing:
    case MedicationOrderStatus.Refunded:
    case MedicationOrderStatus.Canceled: {
      let buttons = null;

      if (hasAmazonPriorAuth) {
        const amazonButton = renderCta(
          isMobile,
          "Go to Amazon",
          "filled",
          setAmazonDetails
        );
        buttons = (
          <div style={{ width: "100%", textAlign: "center" }}>
            <Flex align="center" justify="center" gap="8px" mb="sm">
              <IonIcon
                icon={informationCircleOutline}
                style={{ fontSize: 26 }}
              />
              <Text fz="14px" fw={400} lh="1" ta="center">
                Order your medication on Amazon
              </Text>
            </Flex>
            {amazonButton}
          </div>
        );
      }

      if (!hideShowDetailsBtn) {
        buttons = (
          <>
            {buttons}
            {renderCta(
              isMobile,
              "View Details",
              "light",
              handleViewDetails
            )}{" "}
          </>
        );
      }

      return buttons;
    }
    case MedicationOrderStatus.Shipping: {
      let trackOrderBtn: JSX.Element | null = null;

      if (prescription && prescription.tracking && prescription.carrier) {
        const trackingUrl =
          prescription.carrier.toLowerCase() === "fedex"
            ? `https://www.fedex.com/fedextrack/?tracknumbers=${prescription.tracking}`
            : `https://www.ups.com/track?tracknum=${prescription.tracking}`;
        trackOrderBtn = renderCta(isMobile, "Track Order", "filled", () =>
          inAppBrowser.open(trackingUrl)
        );
      }
      return (
        <>
          {trackOrderBtn}
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
    }
    case MedicationOrderStatus.Delivered:
      return (
        <>
          {renderCta(isMobile, "Confirm Delivery", "filled", () =>
            handleOpenDeliveryModal
              ? handleOpenDeliveryModal(MedicationOrderStatus.Delivered)
              : {}
          )}
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
    case MedicationOrderStatus.ConfirmedDelivered:
      return (
        <>
          {renderCta(isMobile, "Report an Issue", "filled", () => openChat())}
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
    case MedicationOrderStatus.OnHoldForLabs:
      return (
        <>
          {renderCta(isMobile, "Start Lab Work", "filled", () =>
            navContext.navigate("/start-lab-work")
          )}
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
    case MedicationOrderStatus.OnHoldForPayment:
      return (
        <>
          {renderCta(isMobile, "Review Payment Method", "filled", () =>
            navContext.navigate("/payment-methods")
          )}
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
    case MedicationOrderStatus.RequiresConfirmation:
      return (
        <>
          {renderCta(isMobile, "Confirm Delivery", "filled", () =>
            handleOpenDeliveryModal ? handleOpenDeliveryModal(status) : {}
          )}
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
    case MedicationOrderStatus.ProcessingCard:
      return (
        <>
          <Space h={10} />
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
    case MedicationOrderStatus.AsyncOnHold:
      return (
        <>
          {renderCta(isMobile, "Chat with Provider", "filled", () =>
            navContext.navigate("/messages")
          )}
          {!hideShowDetailsBtn &&
            renderCta(isMobile, "View Details", "light", setShowDetails)}
        </>
      );
  }
};

const renderDetailsHeader = (icon: IconDefinition, text: string) => {
  return (
    <Text ta="start" fw={600} fz="16px">
      <FontAwesomeIcon icon={icon} size="lg" style={{ marginRight: "15px" }} />
      {text}
    </Text>
  );
};

const detailsHeader = (status: MedicationOrderStatus) => {
  switch (status) {
    case MedicationOrderStatus.OnHoldForLabs:
    case MedicationOrderStatus.OnHoldForPayment:
      return renderDetailsHeader(faExclamationCircle, "Order On Hold");
    case MedicationOrderStatus.Pending:
    case MedicationOrderStatus.AsyncConfirmed:
      return renderDetailsHeader(faLoader, "Order Pending");
    case MedicationOrderStatus.Shipping:
      return renderDetailsHeader(faTruckFast, "Order Shipping");
    case MedicationOrderStatus.Delivered:
      return renderDetailsHeader(faLocationDot, "Order Delivered");
    case MedicationOrderStatus.RequiresConfirmation:
      return renderDetailsHeader(faExclamation, "Order Requires Confirmation");
    case MedicationOrderStatus.Refunded:
      return renderDetailsHeader(faExclamationCircle, "Order Refunded");
    case MedicationOrderStatus.Canceled:
      return renderDetailsHeader(faExclamationCircle, "Order Canceled");
    default:
    case MedicationOrderStatus.Processing:
      return renderDetailsHeader(faBox, "Order Processing");
  }
};

const renderDetailsText = (text: string) => {
  return (
    <Text fz="14px" fw={400} lh="22px">
      {text}
    </Text>
  );
};

const detailsText = (status: MedicationOrderStatus) => {
  switch (status) {
    case MedicationOrderStatus.AsyncConfirmed:
      return renderDetailsText(
        "Good news! Your medication has been ordered and the pharmacy will start working on it soon. We'll update you when it's being processed."
      );
    case MedicationOrderStatus.OnHoldForLabs:
      return renderDetailsText(
        "Your clinician needs some lab work before moving forward. This helps make sure treatments are safe and right for you. Your order is on hold until we get your lab results, so be sure to complete it as soon as possible"
      );
    case MedicationOrderStatus.OnHoldForPayment:
      return renderDetailsText(
        "Your medication is ready to be processed, but we need you to update your payment method to avoid delays."
      );
    case MedicationOrderStatus.Pending:
      return renderDetailsText(
        "Good news! Your medication has been ordered and the pharmacy will start working on it soon. We'll update you when it's being processed."
      );
    case MedicationOrderStatus.Shipping:
      return renderDetailsText(
        "Great news! Your medication is on its way, and we'll send over tracking info soon. Keep an eye out for your medication!"
      );
    case MedicationOrderStatus.ConfirmedDelivered:
    case MedicationOrderStatus.Delivered:
      return renderDetailsText(
        "Your medication has been delivered! If there's any problem with your order please let us know as soon as possible. We're here to help you achieve your health goals!"
      );
    case MedicationOrderStatus.RequiresConfirmation:
      return renderDetailsText(
        "Did your medication arrive? Please let us know by either confirming or reporting an issue. This helps us make sure you have what you need to achieve your health goals."
      );
    case MedicationOrderStatus.Refunded:
      return renderDetailsText(
        `Your order has been refunded which means you won't receive your prescribed medication. If you think this is a mistake, please call our support team at ${FORMATTED_PHONE_NUMBER}.`
      );
    case MedicationOrderStatus.Canceled:
      return renderDetailsText(
        `Your order was canceled which means you won't get your prescribed medication. If you think this was a mistake, please, call our support team at ${FORMATTED_PHONE_NUMBER}.`
      );
    default:
    case MedicationOrderStatus.Processing:
      return renderDetailsText(
        "Your medication order is now being prepared. The pharmacy is working on it, and it should ship in 3-5 business days. We'll let you know when it's on the way."
      );
  }
};

export const getOrderDetails = (status: MedicationOrderStatus) => {
  return (
    <Stack>
      <Card shadow="none" padding="lg" radius="lg" withBorder>
        <Stack>
          {detailsHeader(status)}
          {detailsText(status)}
        </Stack>
      </Card>
      {
        <Text ta="start" fw={500} fz="28px">
          <FontAwesomeIcon
            icon={faCircleInfoRegular}
            size="sm"
            style={{ marginRight: "15px" }}
          />
          Policy Information
        </Text>
      }

      <Card shadow="none" padding="lg" radius="lg" withBorder>
        <Stack>
          {renderDetailsHeader(
            faPersonCircleQuestion,
            "Refund & Cancelation Policy"
          )}
          {renderDetailsText(
            "Need to cancel or refund your prescription order? Please be aware that orders can only be canceled and refunded before they have been processed by our partner pharmacy. Once a medication has been prescribed and processed, we are unable to cancel or refund the order. This policy ensures the safety and accuracy of our prescription process."
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export const SMDNDCToCommonName = (ndc: string) => {
  switch (ndc) {
    case "54288082601":
    case "54288083301":
    case "54288082701":
    case "54288083101":
      return Medication.semaglutide;
    case "54288083501":
    case "54288083201":
    case "54288083601":
      return Medication.tirzepatide;
    case "00002250680":
    case "00002249580":
    case "00002248480":
    case "00002247180":
    case "00002246080":
    case "00002245780":
      return Medication.zepbound;
    case "00169418113":
    case "00169413013":
    case "00169477212":
      return Medication.ozempic;
    default:
      return Medication.metformin;
  }
};

export const getMedicationOrderCta = (
  status: MedicationOrderStatus,
  hasAmazonPriorAuth: boolean
) => {
  switch (status) {
    case MedicationOrderStatus.AsyncConfirmed:
      return "Your medication is confirmed!";
    case MedicationOrderStatus.OnHoldForLabs:
      return "Please complete your labs.";
    case MedicationOrderStatus.OnHoldForPayment:
      return "Please update your payment.";
    case MedicationOrderStatus.Pending:
      return hasAmazonPriorAuth
        ? "Your prescription has been sent"
        : "Your medication has been ordered!";
    case MedicationOrderStatus.Processing:
      return "The pharmacy is processing your Rx.";
    case MedicationOrderStatus.Shipping:
      return "Your meds are on the way!";
    case MedicationOrderStatus.Delivered:
    case MedicationOrderStatus.RequiresConfirmation:
      return "Did your meds arrive?";
    case MedicationOrderStatus.Refunded:
      return "We've processed your refund.";
    case MedicationOrderStatus.Canceled:
      return "We've canceled your order.";
    case MedicationOrderStatus.ConfirmedDelivered:
      return "Your meds were delivered.";
    case MedicationOrderStatus.ProcessingCard:
      return "We are processing your payment.";
    default:
      return "";
  }
};
